/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Return URL helpers
 *
 * Resolves application-relative return routes against the current application
 * origin so identity-provider redirects can never leave the application.
 */

/** Default application route opened after a successful login. */
export const DEFAULT_LOGIN_RETURN_URL = '/app';

/** Default application route opened after a completed logout. */
export const DEFAULT_LOGOUT_RETURN_URL = '/login#';

/**
 * Resolves a return route to an absolute URL on the current application origin.
 *
 * Routes that resolve to a foreign origin (for example `https://evil` or
 * `//evil`) or that cannot be parsed are replaced by the fallback route so the
 * identity provider never redirects the browser outside of the application.
 *
 * @param returnUrl - The optional application-relative return route.
 * @param fallbackUrl - The application-relative route used when the return route is missing or unsafe.
 * @returns An absolute URL on the current application origin.
 */
export function resolveReturnUrl(
  returnUrl: string | undefined,
  fallbackUrl: string = DEFAULT_LOGIN_RETURN_URL,
): string {
  const applicationOrigin = window.location.origin;
  const fallback = new URL(fallbackUrl, applicationOrigin);

  if (!returnUrl) {
    return fallback.toString();
  }

  try {
    const resolved = new URL(returnUrl, applicationOrigin);
    return resolved.origin === applicationOrigin
      ? resolved.toString()
      : fallback.toString();
  } catch {
    return fallback.toString();
  }
}

/**
 * Resolves a return route to a safe application-relative path.
 *
 * Applies the same origin check as {@link resolveReturnUrl} and returns only
 * the path, query, and fragment so the value can be stored as application
 * state (for example the Auth0 `appState.target`).
 *
 * @param returnUrl - The optional application-relative return route.
 * @param fallbackUrl - The application-relative route used when the return route is missing or unsafe.
 * @returns A path starting with `/` on the current application origin.
 */
export function resolveRelativeReturnUrl(
  returnUrl: string | undefined,
  fallbackUrl: string = DEFAULT_LOGIN_RETURN_URL,
): string {
  const resolved = new URL(resolveReturnUrl(returnUrl, fallbackUrl));
  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}
